import { useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { Switch } from "./ui/switch";
import { uploadFile, fileUrl, notifyErr } from "../lib/api";

const BTN = {
  primary: "bg-[#220b48] hover:bg-[#34146b] text-white",
  lime: "bg-[#15fabc] hover:bg-[#0fe3aa] text-[#220b48]",
  secondary: "bg-white hover:bg-[#EEF0E9] text-[#1B1D17] border border-[#DDE0D5]",
  ghost: "hover:bg-[#EEF0E9] text-[#3D4136]",
  danger: "bg-red-50 hover:bg-red-100 text-red-700 border border-red-200",
};

export const Btn = ({ variant = "primary", loading, className = "", children, ...p }) => (
  <button
    {...p}
    disabled={loading || p.disabled}
    className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-[background-color,transform,opacity] duration-150 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none ${BTN[variant]} ${className}`}
  >
    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
    {children}
  </button>
);

export const inputCls =
  "w-full bg-white border border-[#DDE0D5] rounded-xl px-3.5 py-2.5 text-[#1B1D17] placeholder-[#A0A397] focus:outline-none focus:border-[#220b48] focus:ring-2 focus:ring-[#15fabc]/60 text-sm transition-colors disabled:bg-[#F3F4EF]";

export const Input = (p) => <input {...p} className={`${inputCls} ${p.className || ""}`} />;
export const Textarea = (p) => <textarea rows={3} {...p} className={`${inputCls} ${p.className || ""}`} />;
export const Select = ({ options, ...p }) => (
  <select {...p} className={`${inputCls} ${p.className || ""}`}>
    {options.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>))}
  </select>
);

export const Field = ({ label, hint, children, className = "" }) => (
  <label className={`block space-y-1.5 ${className}`}>
    {label && <span className="text-xs font-medium text-[#62665C]">{label}</span>}
    {children}
    {hint && <span className="block text-xs text-zinc-500">{hint}</span>}
  </label>
);

export const Card = ({ className = "", children, ...p }) => (
  <div {...p} className={`bg-white border border-[#E9EBE3] rounded-2xl p-5 ${className}`}>{children}</div>
);

export const PageHeader = ({ title, sub, children }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 rise">
    <div>
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1B1D17]">{title}</h1>
      {sub && <p className="text-[#62665C] mt-1.5 text-sm md:text-base">{sub}</p>}
    </div>
    <div className="flex gap-2 flex-wrap">{children}</div>
  </div>
);

export const Badge = ({ tone = "zinc", children, ...p }) => {
  const t = { zinc: "bg-[#EEF0E9] text-[#4A4E43]", green: "bg-[#D2FEF1] text-[#0B7A5C]", amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-700", blue: "bg-sky-100 text-sky-800" }[tone];
  return <span {...p} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${t} ${p.className || ""}`}>{children}</span>;
};

export const Empty = ({ icon: Icon, title, sub, children }) => (
  <div className="bg-white border border-dashed border-[#D6D9CE] rounded-2xl py-16 px-6 text-center">
    {Icon && <Icon className="h-8 w-8 mx-auto text-zinc-600 mb-3" />}
    <p className="font-semibold">{title}</p>
    {sub && <p className="text-sm text-zinc-500 mt-1">{sub}</p>}
    <div className="mt-5 flex justify-center gap-2">{children}</div>
  </div>
);

export const Toggle = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center justify-between gap-3 py-1.5 text-sm">
    <span className="text-zinc-300">{label}</span>
    <Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} />
  </label>
);

export function ImageInput({ value, onChange, testid, label = "Upload" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadFile(f)); } catch (err) { notifyErr(err); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-3">
      {value ? <img src={fileUrl(value)} alt="" className="h-12 w-12 rounded-lg object-cover border border-zinc-700" /> : null}
      <Btn type="button" variant="secondary" loading={busy} onClick={() => ref.current.click()} data-testid={testid} className="py-2">
        <Upload className="h-4 w-4" /> {label}
      </Btn>
      {value && <button type="button" onClick={() => onChange(null)} className="text-zinc-500 hover:text-red-400" data-testid={`${testid}-clear`}><X className="h-4 w-4" /></button>}
      <input ref={ref} type="file" accept="image/*" hidden onChange={pick} />
    </div>
  );
}

export const ColourInput = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="h-10 w-12 rounded-lg bg-transparent border border-[#DDE0D5] cursor-pointer" data-testid={testid} />
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="font-mono" />
  </div>
);

export const Table = ({ cols, rows, empty = "Nothing here yet.", testid }) => (
  <div className="overflow-x-auto bg-white border border-[#E9EBE3] rounded-2xl" data-testid={testid}>
    <table className="w-full text-sm">
      <thead className="text-[#8C9085] text-xs">
        <tr>{cols.map((c) => <th key={c[0]} className="text-left font-medium px-4 py-3 whitespace-nowrap">{c[0]}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-zinc-500">{empty}</td></tr>}
        {rows.map((r, i) => (
          <tr key={r.id || i} className="border-t border-[#EEF0E9] hover:bg-[#F8F9F5] transition-colors">
            {cols.map((c) => <td key={c[0]} className="px-4 py-3 align-middle">{c[1](r)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
